import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { ShieldCheck, Loader2, Copy, Check } from "lucide-react";
import { API } from "../App";
import { useI18n } from "../lib/i18n";

const TwoFactorSetupPage = ({ setUser }) => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const tempToken = searchParams.get("temp");

  const [qrCode, setQrCode] = useState("");
  const [secret, setSecret] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchSetup = async () => {
      try {
        // Use temp token if available (pre-login flow), otherwise authenticated
        if (tempToken) {
          const res = await axios.post(`${API}/auth/2fa/setup-with-token`, {
            temp_token: tempToken, code: "",
          });
          setQrCode(res.data.qr_code);
          setSecret(res.data.secret);
        } else {
          const res = await axios.post(`${API}/auth/2fa/setup`);
          setQrCode(res.data.qr_code);
          setSecret(res.data.secret);
        }
      } catch (err) {
        toast.error(err.response?.data?.detail || "Erreur lors de la configuration 2FA");
        navigate("/login");
      } finally {
        setLoading(false);
      }
    };
    fetchSetup();
  }, [tempToken, navigate]);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (code.length !== 6) {
      toast.error("Veuillez entrer un code à 6 chiffres");
      return;
    }
    setVerifying(true);
    try {
      const res = await axios.post(`${API}/auth/2fa/verify-setup`, {
        temp_token: tempToken,
        code,
      });
      if (res.data.session_token) {
        localStorage.setItem("session_token", res.data.session_token);
      }
      setUser(res.data);
      toast.success("2FA activé avec succès !");
      navigate("/therapist");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Code invalide");
      setCode("");
    } finally {
      setVerifying(false);
    }
  };

  const copySecret = () => {
    navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4" data-testid="2fa-setup-page">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-3">
            <ShieldCheck className="w-7 h-7 text-primary" />
          </div>
          <CardTitle className="font-heading text-2xl">
            Configuration 2FA
          </CardTitle>
          <CardDescription>
            La double authentification est obligatoire pour les thérapeutes. Scannez le QR code avec votre application d'authentification (Google Authenticator, Authy, etc.)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* QR Code */}
          {qrCode && (
            <div className="flex justify-center">
              <div className="bg-white p-3 rounded-xl shadow-sm">
                <img
                  src={qrCode}
                  alt="QR Code 2FA"
                  className="w-52 h-52"
                  data-testid="2fa-qr-code"
                />
              </div>
            </div>
          )}

          {/* Manual entry fallback */}
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground text-center">
              Ou entrez cette clé manuellement :
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs bg-muted p-2.5 rounded-lg font-mono text-center break-all" data-testid="2fa-secret">
                {secret}
              </code>
              <Button variant="ghost" size="icon" onClick={copySecret} data-testid="2fa-copy-secret">
                {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          {/* Verification form */}
          <form onSubmit={handleVerify} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Code de vérification</label>
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                className="text-center text-2xl tracking-[0.5em] font-mono"
                autoFocus
                data-testid="2fa-setup-code-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={verifying || code.length !== 6}
              data-testid="2fa-setup-verify-btn"
            >
              {verifying ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Vérification...</>
              ) : (
                <><ShieldCheck className="w-4 h-4 mr-2" /> Activer la double authentification</>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default TwoFactorSetupPage;
